import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, PageHeader, StatusBadge } from "@/components/shared/Common";
import { FilesPanel } from "@/components/shared/Records";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { OPP_STATUS, OT_TONE, clp } from "@/lib/format";
import { ClientForm } from "./Clients";

const Contacts = ({ client, canEdit, onChanged }) => {
  const [f, setF] = useState({ nombre: "", cargo: "", email: "", telefono: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/gerencia/clients/${client.id}/contacts`, f); setF({ nombre: "", cargo: "", email: "", telefono: "" }); onChanged(); toast.success("Contacto agregado"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/gerencia/contacts/${c.id}`, c, { params: { active: !c.active } }); onChanged(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="syc-card overflow-hidden" data-testid="contacts-panel">
      {canEdit && (
        <form onSubmit={add} className="grid sm:grid-cols-5 gap-2 p-4 border-b border-[#e4e8e1] bg-[#fafbf9]">
          {["nombre", "cargo", "email", "telefono"].map((k) => <Input key={k} data-testid={`contact-${k}-input`} placeholder={k[0].toUpperCase() + k.slice(1)} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} required={k === "nombre"} />)}
          <Button data-testid="add-contact-btn" className="rounded-full"><Plus size={15} className="mr-1" />Agregar</Button>
        </form>
      )}
      {client.contacts.length === 0 ? <EmptyState title="Sin contactos" /> : client.contacts.map((c) => (
        <div key={c.id} className={`flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] text-sm ${c.active ? "" : "opacity-50"}`} data-testid={`contact-row-${c.id}`}>
          <b>{c.nombre}</b><span className="text-[#5b6656]">{c.cargo}</span><span>{c.email}</span><span>{c.telefono}</span>
          {canEdit && <Button size="sm" variant="ghost" className="ml-auto" data-testid={`contact-toggle-${c.id}`} onClick={() => toggle(c)}>{c.active ? "Desactivar" : "Activar"}</Button>}
        </div>
      ))}
    </div>
  );
};

const Related = ({ items, base, labelFn, testid }) => (
  <div className="syc-card overflow-hidden" data-testid={testid}>
    {items.length === 0 ? <EmptyState title="Sin registros" /> : items.map((o) => (
      <Link key={o.id} to={`${base}/${o.id}`} className="flex items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] hover:bg-[#f6fbf2] text-sm">
        <span className="font-mono text-xs">{o.code}</span><span className="flex-1 truncate">{o.title}</span>{labelFn(o)}
      </Link>
    ))}
  </div>
);

export default function ClientDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get(`/gerencia/clients/${id}`).then((r) => { setC(r.data); setForm(r.data); }), [id]);
  useEffect(() => { load(); }, [load]);
  if (!c) return <div className="text-[#5b6656]">Cargando…</div>;
  const canEdit = can("gerencia.clientes.gestionar") || can("gerencia.ventas.crear");

  const save = async () => {
    try { await api.patch(`/gerencia/clients/${id}`, Object.fromEntries(Object.entries(form).filter(([k]) => ["rut", "razon_social", "nombre_fantasia", "giro", "direccion", "comuna", "ciudad", "telefono", "email"].includes(k)))); toast.success("Cliente actualizado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async () => { try { await api.post(`/gerencia/clients/${id}/toggle`); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="client-detail-page">
      <Link to="/gerencia/clientes" className="inline-flex items-center gap-1 text-sm font-semibold text-[#258F00] mb-4" data-testid="back-to-clients-link"><ArrowLeft size={15} />Clientes</Link>
      <PageHeader eyebrow={<StatusBadge status={c.status} label={c.status === "activo" ? "Activo" : "Inactivo"} />} title={c.razon_social} subtitle={`RUT ${c.rut}`}
        actions={can("gerencia.clientes.gestionar") && <Button data-testid="toggle-client-btn" variant="outline" className="rounded-full" onClick={toggle}>{c.status === "activo" ? "Desactivar" : "Activar"}</Button>} />
      <Tabs defaultValue="datos">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="datos" data-testid="client-tab-datos">Datos</TabsTrigger>
          <TabsTrigger value="contactos" data-testid="client-tab-contactos">Contactos ({c.contacts.length})</TabsTrigger>
          {can("gerencia.ventas.ver") && <TabsTrigger value="ventas" data-testid="client-tab-ventas">Ventas ({c.opportunities.length})</TabsTrigger>}
          {can("gerencia.ot.ver") && <TabsTrigger value="ots" data-testid="client-tab-ots">OT ({c.ots.length})</TabsTrigger>}
          <TabsTrigger value="archivos" data-testid="client-tab-archivos">Archivos</TabsTrigger>
        </TabsList>
        <TabsContent value="datos"><div className="syc-card p-6 space-y-4 max-w-3xl"><ClientForm value={form} onChange={setForm} disabled={!canEdit} />{canEdit && <Button data-testid="save-client-btn" className="rounded-full" onClick={save}>Guardar</Button>}</div></TabsContent>
        <TabsContent value="contactos"><Contacts client={c} canEdit={canEdit} onChanged={load} /></TabsContent>
        <TabsContent value="ventas"><Related testid="client-sales" items={c.opportunities} base="/gerencia/ventas" labelFn={(o) => <><span className="text-xs">{clp(o.total)}</span><StatusBadge status={OPP_STATUS[o.status].tone} label={OPP_STATUS[o.status].label} /></>} /></TabsContent>
        <TabsContent value="ots"><Related testid="client-ots" items={c.ots} base="/gerencia/ot" labelFn={(o) => <StatusBadge status={OT_TONE[o.status]} label={o.status} />} /></TabsContent>
        <TabsContent value="archivos"><FilesPanel resourceType="client" resourceId={id} canWrite={can("gerencia.ventas.editar") || can("gerencia.clientes.gestionar")} /></TabsContent>
      </Tabs>
    </div>
  );
}
